import { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { format, parseISO, isValid } from "date-fns/esm";
import { Download, FileText } from "lucide-react";

import { Loader } from "components/Loader";
import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { useSurmountDocuments } from "features/Dashboard/hook";
import { getSurmountAccountDocumentDetails } from "features/Dashboard/service";
import {
  PanelContainer,
  DownloadButton,
  MoreButton,
} from "./DocumentsPanel.styles";

const parseDate = (value) => {
  const date = parseISO(String(value || "").slice(0, 10));
  return isValid(date) ? date : null;
};

// The backend sends each document as { id, name, date }, where the name is the
// Alpaca document type ("Account Statement", "Tax Statement", "Trade
// Confirmation"). Read the kind from it (or from `type` when one is sent).
const kindOf = (doc) => {
  const text = `${doc.type || ""} ${doc.name || ""}`.toLowerCase();
  if (text.includes("account") && text.includes("statement"))
    return "statement";
  if (text.includes("tax") || text.includes("1099")) return "tax";
  return "other";
};

// "Tax 1099 B" -> "Form 1099-B"; anything else keeps the backend's name.
const taxFormName = (name) => {
  const match = String(name || "").match(/1099[\s_-]*([a-z]{1,4})\b/i);
  return match ? `Form 1099-${match[1].toUpperCase()}` : name;
};

// What the Type column shows: the kind of document, and under it the month of
// a statement or the name of a tax form.
const describe = (doc) => {
  const date = parseDate(doc.date);
  const kind = kindOf(doc);
  if (kind === "statement") {
    return {
      label: "Account statement",
      detail: date ? format(date, "MMMM yyyy") : "",
    };
  }
  if (kind === "tax") {
    return { label: "Tax document", detail: taxFormName(doc.name) };
  }
  return { label: doc.name || "Document", detail: "" };
};

const DownloadDocument = ({ doc }) => {
  const dispatch = useDispatch();
  const [isBusy, setIsBusy] = useState(false);

  const handleDownload = async () => {
    setIsBusy(true);
    // Open the tab now, while the click still counts, so the browser does not
    // block it after the request; point it at the document once the link is in.
    const tab = window.open("", "_blank");
    try {
      const response = await getSurmountAccountDocumentDetails(doc.id);
      if (response?.data?.url && tab) {
        tab.location.href = response.data.url;
      } else if (response?.data?.url) {
        window.open(response.data.url, "_blank");
      } else {
        tab?.close();
        dispatch(toast({ type: "warning", body: "Document URL not found" }));
      }
    } catch (error) {
      tab?.close();
      dispatch(toast({ type: "error", body: parseErrorMessage(error) }));
    } finally {
      setIsBusy(false);
    }
  };

  const { label, detail } = describe(doc);
  return (
    <DownloadButton
      type="button"
      onClick={handleDownload}
      disabled={isBusy}
      aria-label={`Download ${label}${detail ? `, ${detail}` : ""}`}
    >
      {isBusy ? <Loader className="on-btn" /> : <Download size={14} />}
      Download
    </DownloadButton>
  );
};

DownloadDocument.propTypes = {
  doc: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string,
    name: PropTypes.string,
    date: PropTypes.string,
  }).isRequired,
};

export const DocumentsPanel = () => {
  const {
    documents,
    isLoading,
    error,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useSurmountDocuments();
  // A user with no Surmount account yet gets a 404: an empty list, not a failure.
  const hasFailed = Boolean(error) && error?.response?.status !== 404;

  return (
    <PanelContainer>
      <h3 className="panel-heading">Documents</h3>
      <p className="panel-subtitle">
        Your tax forms and monthly account statements.
      </p>

      <div className="documents-table" role="table" aria-label="Documents">
        <div className="documents-head" role="row">
          <span role="columnheader">Date</span>
          <span role="columnheader">Type</span>
          <span role="columnheader" className="download-col">
            Download
          </span>
        </div>

        {isLoading && (
          <div className="documents-state">
            <Loader />
          </div>
        )}
        {!isLoading && hasFailed && (
          <div className="documents-state" role="alert">
            We couldn&apos;t load your documents.{" "}
            <button type="button" className="retry" onClick={() => refetch()}>
              Try again
            </button>
          </div>
        )}
        {!isLoading && !hasFailed && documents.length === 0 && (
          <div className="documents-state">No documents available</div>
        )}

        {documents.map((doc) => {
          const date = parseDate(doc.date);
          const { label, detail } = describe(doc);
          return (
            <div className="documents-row" role="row" key={doc.id}>
              <span className="doc-date" role="cell">
                {date ? format(date, "MMM d, yyyy") : doc.date}
              </span>
              <span className="doc-type" role="cell">
                <FileText size={20} strokeWidth={1.5} aria-hidden="true" />
                <span className="doc-type-text">
                  <span className="doc-type-label">{label}</span>
                  {detail && <span className="doc-type-detail">{detail}</span>}
                </span>
              </span>
              <span className="doc-download" role="cell">
                <DownloadDocument doc={doc} />
              </span>
            </div>
          );
        })}
      </div>

      {hasNextPage && (
        <div className="show-more">
          <MoreButton
            type="button"
            onClick={fetchNextPage}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? <Loader className="on-btn" /> : "Show more"}
          </MoreButton>
        </div>
      )}
    </PanelContainer>
  );
};
